import axios, { AxiosInstance } from 'axios';
import { getAuthToken } from './auth';
import type {
  Configuration,
  ConfigurationCreate,
  ConfigurationUpdate,
  IntegrationToken,
  IntegrationTokenCreate,
  IntegrationTokenCreated,
  IntegrationTokenUpdate,
  Me,
  Session,
  SessionCreate,
  SessionListItem,
  User,
  UserCreate,
  UserUpdate,
  VehicleCategory,
  VehicleCategoryCreate,
  VehicleCategoryUpdate,
} from '@transpordiamet/shared';

/** Steps whose illustrations could not be copied during configuration duplicate. */
export type SkippedIllustration = {
  stepId: string;
  reason: 'unresolvable_url' | 'object_missing';
};

export type DuplicateConfigurationResult = Configuration & {
  skippedIllustrations?: SkippedIllustration[];
};

class AdminAPI {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });

    // Add auth token to all requests
    this.client.interceptors.request.use((config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Authenticate with email and password
   */
  async authenticate(email: string, password: string): Promise<{ success: boolean; message: string; token: string }> {
    // Use axios directly without auth interceptor
    const response = await this.client.post('/authenticate', { email, password });
    return response.data;
  }

  /**
   * Revoke the current login token on the server
   */
  async logout(): Promise<void> {
    // Empty body so Axios sets Content-Type: application/json (required by backend)
    await this.client.post('/logout', {});
  }

  /**
   * The logged-in user's role and label permissions
   */
  async getMe(): Promise<Me> {
    const response = await this.client.get<Me>('/me');
    return response.data;
  }

  /**
   * Labels the caller can assign (admins: every label in use)
   */
  async listLabels(): Promise<string[]> {
    const response = await this.client.get<string[]>('/labels');
    return response.data;
  }

  /**
   * Admin console users (admin only)
   */
  async listUsers(): Promise<User[]> {
    const response = await this.client.get<User[]>('/users');
    return response.data;
  }

  async createUser(data: UserCreate): Promise<User> {
    const response = await this.client.post<User>('/users', data);
    return response.data;
  }

  async updateUser(id: string, data: UserUpdate): Promise<User> {
    const response = await this.client.put<User>(`/users/${id}`, data);
    return response.data;
  }

  /**
   * Integration (API) tokens (admin only). The plain token is only in the create response.
   */
  async listTokens(): Promise<IntegrationToken[]> {
    const response = await this.client.get<IntegrationToken[]>('/tokens');
    return response.data;
  }

  async createToken(data: IntegrationTokenCreate): Promise<IntegrationTokenCreated> {
    const response = await this.client.post<IntegrationTokenCreated>('/tokens', data);
    return response.data;
  }

  async updateToken(id: string, data: IntegrationTokenUpdate): Promise<IntegrationToken> {
    const response = await this.client.put<IntegrationToken>(`/tokens/${id}`, data);
    return response.data;
  }

  /**
   * Get all configurations
   */
  async getAllConfigurations(): Promise<Configuration[]> {
    const response = await this.client.get<Configuration[]>('/configurations');
    return response.data;
  }

  /**
   * Get a single configuration by ID
   */
  async getConfiguration(id: string): Promise<Configuration> {
    const response = await this.client.get<Configuration>(`/configurations/${id}`);
    return response.data;
  }

  /**
   * Create a new configuration
   */
  async createConfiguration(data: ConfigurationCreate): Promise<Configuration> {
    const response = await this.client.post<Configuration>('/configurations', data);
    return response.data;
  }

  /**
   * Update configuration by ID
   */
  async updateConfiguration(id: string, data: ConfigurationUpdate): Promise<Configuration> {
    const response = await this.client.put<Configuration>(`/configurations/${id}`, data);
    return response.data;
  }

  /**
   * Soft-delete configuration by ID (hidden from admin; historical sessions keep snapshots)
   */
  async deleteConfiguration(id: string): Promise<void> {
    await this.client.delete(`/configurations/${id}`);
  }

  /**
   * Duplicate configuration (copies step illustrations in R2 when possible)
   */
  async duplicateConfiguration(id: string): Promise<DuplicateConfigurationResult> {
    // Empty body so Axios sets Content-Type: application/json (required by backend)
    const response = await this.client.post<DuplicateConfigurationResult>(`/configurations/${id}/duplicate`, {});
    return response.data;
  }

  /**
   * Create a new session
   */
  async createSession(data: SessionCreate): Promise<Session> {
    const response = await this.client.post<Session>('/sessions', data);
    return response.data;
  }

  /**
   * Live sessions (admin console login token only)
   */
  async listSessions(): Promise<SessionListItem[]> {
    const response = await this.client.get<SessionListItem[]>('/sessions');
    return response.data;
  }

  /**
   * Session with photos (the public GET; the session ID is the credential)
   */
  async getSession(id: string): Promise<Session> {
    const response = await this.client.get<Session>(`/sessions/${id}`);
    return response.data;
  }

  /**
   * Request presigned URL for configuration upload (e.g., illustrations)
   */
  async getConfigurationPresignUrl(
    configurationId: string,
    r2Path: string
  ): Promise<{ uploadURL: string; key: string; expiresIn: number; publicUrl?: string }> {
    const response = await this.client.post<{ uploadURL: string; key: string; expiresIn: number; publicUrl?: string }>(
      `/configurations/${configurationId}/upload/presign`,
      { r2Path }
    );
    return response.data;
  }

  /**
   * Vehicle categories CRUD
   */
  async listVehicleCategories(): Promise<VehicleCategory[]> {
    const response = await this.client.get<VehicleCategory[]>('/categories');
    return response.data;
  }

  async getVehicleCategory(id: string): Promise<VehicleCategory> {
    const response = await this.client.get<VehicleCategory>(`/categories/${id}`);
    return response.data;
  }

  async createVehicleCategory(data: VehicleCategoryCreate): Promise<VehicleCategory> {
    const response = await this.client.post<VehicleCategory>('/categories', data);
    return response.data;
  }

  async updateVehicleCategory(id: string, data: VehicleCategoryUpdate): Promise<VehicleCategory> {
    const response = await this.client.put<VehicleCategory>(`/categories/${id}`, data);
    return response.data;
  }

  async deleteVehicleCategory(id: string): Promise<void> {
    await this.client.delete(`/categories/${id}`);
  }
}

export const adminAPI = new AdminAPI();
